/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import {
  useConnectPostmaster,
  useDisconnectPostmaster,
  useGetConnectionStatus,
} from "@/hooks/use-postmaster";
import { usePermission } from "@/hooks/use-premission";
import { PostmasterAccount } from "@/types/postman.types";
import { format } from "date-fns";
import { Globe, Info } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import InitialPageVisitGuideModal from "../page-visit-guide";
import { ConfirmActionPopup } from "../popups/confirm-action";
import { Alert, AlertDescription } from "../ui/alert";
import { Card } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import Trash from "../icons/trash";

export const PostmasterIntegrationContent = () => {
  const { hasPermission } = usePermission();

  if (!hasPermission("integration:view")) {
    return null;
  }

  return (
    <div className="">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-4 sm:gap-0">
        <div>
          <h1 className="page-heading">Integration</h1>
          <p className="text-sm text-muted-foreground">
            Integrate seamlessly to access and analyze your email performance
            reports directly in our app.
          </p>
        </div>
      </div>

      <div className="w-full">
        <PostmasterIntegration />
      </div>

      <InitialPageVisitGuideModal type="integration" />
    </div>
  );
};

export const PostmasterIntegration = () => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<PostmasterAccount | null>(null);

  const { hasPermission } = usePermission();
  const { data: connectionStatus, isLoading } = useGetConnectionStatus();
  const connectMutation = useConnectPostmaster();
  const disconnectMutation = useDisconnectPostmaster();

  const handleDisconnect = async () => {
    try {
      if (!selectedAccount) {
        toast.error("No account selected");
        return;
      }

      await disconnectMutation.mutateAsync(selectedAccount?.id);
      toast.success("Postmaster account disconnected successfully");
      window.cioanalytics?.track("postmaster_account_disconnected", {
        email: selectedAccount?.email,
      });

      setShowDeleteConfirm(false);
      setSelectedAccount(null);
    } catch (error) {
      toast.error("Failed to disconnect Postmaster account");
    }
  };

  const handleConnect = async () => {
    try {
      await connectMutation.mutateAsync();
      toast.success("Postmaster account connected successfully");
      window.cioanalytics?.track("postmaster_account_connected", {
        email: selectedAccount?.email,
      });
    } catch (error) {
      toast.error("Failed to connect Postmaster account");
    }
  };

  return (
    <Card className="space-y-4 p-4">
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Syncing domains from Postmaster will take up to 15 minutes.
        </AlertDescription>
      </Alert>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <div>
          <h3 className="text-lg font-medium">Google Postmaster Integration</h3>
          <p className="text-sm text-muted-foreground">
            {connectionStatus?.accounts.length
              ? `${connectionStatus.accounts.length} Postmaster account${
                  connectionStatus.accounts.length > 1 ? "s" : ""
                } connected`
              : "Connect your Google Postmaster accounts to monitor your domains"}
          </p>
        </div>
        {hasPermission("integration:add") && (
          <Button
            onClick={() => handleConnect()}
            disabled={connectMutation.isPending}
            variant="outline"
            className="gap-2 w-full sm:w-auto sm:flex-shrink-0"
          >
            <Globe className="h-4 w-4" />

            {connectMutation.isPending
              ? "Connecting..."
              : connectionStatus?.isConnected
              ? "Add Another Account"
              : "Connect Postmaster"}
          </Button>
        )}
      </div>

      {isLoading && (
        <Card className="flex items-center justify-between p-3">
          <div className="flex flex-col gap-1">
            <Skeleton className="h-[24px] w-[100px]" />
            <Skeleton className="h-[20px] w-[200px]" />
          </div>
          <Skeleton className="h-[32px] w-[32px]" />
        </Card>
      )}

      {/* List connected accounts */}
      {connectionStatus?.accounts.map((account) => (
        <Card key={account.id} className="p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <div>
              <p className="font-medium">Postmaster Account</p>
              <p className="text-sm text-muted-foreground">
                {account.email} (
                {account.createdAt
                  ? format(new Date(account.createdAt), "MMM dd, yyyy hh:mm a")
                  : "N/A"}
                )
              </p>
              {/* <p className="text-sm text-muted-foreground">
                Last synced: {format(new Date(account.lastSyncedDate), 'MMM dd, yyyy HH:mm')}
              </p> */}
            </div>
            {hasPermission("integration:remove") && (
              <Button
                onClick={() => {
                  setSelectedAccount(account);
                  setShowDeleteConfirm(true);
                }}
                variant="ghost"
                size="sm"
                className="w-full sm:w-auto sm:flex-shrink-0 delete-button group"
              >
                <Trash className="h-4 w-4" />
                <span className="sm:hidden ml-2">Remove Account</span>
              </Button>
            )}
          </div>
        </Card>
      ))}

      <ConfirmActionPopup
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Disconnect Postmaster"
        description="Are you sure you want to disconnect this Postmaster account? This action cannot be undone."
        actionLabel="Disconnect"
        onConfirm={async () => handleDisconnect()}
      />
    </Card>
  );
};
